/**
 * 阅读器首进性能计时（页面加载 / 接口 / 正文渲染）
 *
 * @description
 * 时间原点为 navigationStart（与 performance.now() 对齐）。
 * 同一文档生命周期仅上报一次；由正文首帧、失败或超时触发 flush。
 */

import { SESSION_KEY_READER_PERF_ENTRY } from '@/constants/storage';
import { hasSessionReadyCookie } from '@/utils/fetch/session-ready-cookie';
import {
    consumeH5ColdStart,
    getDocumentDclDurationMs,
    getNavElapsedMs,
    logReaderApiLoad,
    logReaderContentRender,
    logReaderMount,
    logReaderPageLoad,
    logReaderProfileHydration,
} from '@/utils/performance-log';
import { collectColdNavigationPerfSnapshot } from '@/utils/performance-log/h5/collect-navigation';
import { sessionStore } from '@/utils/storage';

const RENDER_TIMEOUT_MS = 10_000;

/** sessionStorage 入口快照 */
interface IReaderPerfEntrySnapshot {
    is_from_landing: number;
    is_cold_start: number;
    had_session_cookie: number;
    book_id: string;
    chapter_id: string;
}

interface IReaderPerfState {
    tracking: boolean;
    /** 三步独立上报标记，各自在对应阶段完成后置 true */
    hasReportedPageLoad: boolean;
    hasReportedApiLoad: boolean;
    hasReportedContentRender: boolean;
    /** pagehide 监听是否已注册 */
    pageHideListenerAttached: boolean;
    readerMountMs: number | null;
    pageReadyMs: number | null;
    landingFlowDone: number;
    landingWaitDuration: number | null;
    landingApiDuration: number | null;
    landingApiSuccess: number | null;
    bookDetailDuration: number | null;
    bookDetailSuccess: number | null;
    chapterMetaDuration: number | null;
    chapterMetaSuccess: number | null;
    chapterContentDuration: number | null;
    chapterContentSuccess: number | null;
    bookDetailStartMs: number | null;
    chapterMetaStartMs: number | null;
    chapterContentStartMs: number | null;
    sessionWaitDuration: number | null;
    profileHydrationWaitDuration: number | null;
    failStage: string | null;
    renderTimeout: number;
    renderTimeoutTimer: ReturnType<typeof setTimeout> | null;
    firstChapterApiMarked: boolean;
    entry: IReaderPerfEntrySnapshot;
}

const state: IReaderPerfState = {
    tracking: false,
    hasReportedPageLoad: false,
    hasReportedApiLoad: false,
    hasReportedContentRender: false,
    pageHideListenerAttached: false,
    readerMountMs: null,
    pageReadyMs: null,
    landingFlowDone: 0,
    landingWaitDuration: null,
    landingApiDuration: null,
    landingApiSuccess: null,
    bookDetailDuration: null,
    bookDetailSuccess: null,
    chapterMetaDuration: null,
    chapterMetaSuccess: null,
    chapterContentDuration: null,
    chapterContentSuccess: null,
    bookDetailStartMs: null,
    chapterMetaStartMs: null,
    chapterContentStartMs: null,
    sessionWaitDuration: null,
    profileHydrationWaitDuration: null,
    failStage: null,
    renderTimeout: 0,
    renderTimeoutTimer: null,
    firstChapterApiMarked: false,
    entry: {
        is_from_landing: 0,
        is_cold_start: 1,
        had_session_cookie: 0,
        book_id: '',
        chapter_id: '',
    },
};

function roundMs(value: number): number {
    return Math.round(value);
}

export function resolveIsFromLanding(search: Pick<URLSearchParams, 'get'>): number {
    const hasLandingTimestamp = Boolean(search.get('landing_timestamp'));
    const hasPromotionBundle =
        Boolean(search.get('promotion_code')) &&
        Boolean(search.get('track_id')) &&
        Boolean(search.get('ad_id')) &&
        Boolean(search.get('adset_id'));
    return hasLandingTimestamp || hasPromotionBundle ? 1 : 0;
}

function persistEntrySnapshot(snapshot: IReaderPerfEntrySnapshot): void {
    try {
        sessionStore.setItem(SESSION_KEY_READER_PERF_ENTRY, JSON.stringify(snapshot));
    } catch {
        // 隐私模式等
    }
}

function loadEntrySnapshot(): IReaderPerfEntrySnapshot | null {
    try {
        const raw = sessionStore.getItem(SESSION_KEY_READER_PERF_ENTRY);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw) as IReaderPerfEntrySnapshot;
    } catch {
        return null;
    }
}

function clearRenderTimeout(): void {
    if (state.renderTimeoutTimer != null) {
        clearTimeout(state.renderTimeoutTimer);
        state.renderTimeoutTimer = null;
    }
}

export interface IFlushReaderPerfOptions {
    /** 1 正文 DOM 已绘制；0 无正文（付费墙/空内容/超时） */
    hasBodyContent: number;
    /** navigationStart → 双 rAF（ms）；正文首帧 flush 时传入，作 fp/fcp 缺失参考 */
    rafPaintDuration?: number | null;
}

// ── 三步独立发送（内部函数） ──

/** Step 1: 页面加载耗时（在 markReaderPageReady 或 pagehide 时调用） */
function sendPageLoad(pageHidden = false): void {
    if (state.hasReportedPageLoad || !state.tracking) {
        return;
    }
    state.hasReportedPageLoad = true;

    const pageReadyMs = state.pageReadyMs ?? getNavElapsedMs();
    const readerPageLoadDuration = roundMs(pageReadyMs);

    const navTiming = collectColdNavigationPerfSnapshot();

    logReaderPageLoad({
        book_id: state.entry.book_id,
        chapter_id: state.entry.chapter_id,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        had_session_cookie: state.entry.had_session_cookie,
        landing_flow_done: state.landingFlowDone,
        reader_page_load_duration: readerPageLoadDuration,
        static_load_duration: getDocumentDclDurationMs(),
        landing_wait_duration: state.landingWaitDuration,
        landing_api_duration: state.landingApiDuration,
        landing_api_success: state.landingApiSuccess,
        session_wait_duration: state.sessionWaitDuration,
        page_hidden: pageHidden ? 1 : 0,
        reader_mount_duration: state.readerMountMs,
        ttfb_duration: navTiming.ttfb_duration,
        response_duration: navTiming.response_duration,
        dom_interactive_duration: navTiming.dom_interactive_duration,
        profile_hydration_wait_duration: state.profileHydrationWaitDuration,
    });
}

/** Step 2: 接口加载耗时（在 markReaderPageReady 或 pagehide 时调用） */
function sendApiLoad(): void {
    if (state.hasReportedApiLoad || !state.tracking) {
        return;
    }
    state.hasReportedApiLoad = true;

    logReaderApiLoad({
        book_id: state.entry.book_id,
        chapter_id: state.entry.chapter_id,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        load_type: 'initial',
        book_detail_duration: state.bookDetailDuration,
        book_detail_success: state.bookDetailSuccess,
        chapter_meta_duration: state.chapterMetaDuration,
        chapter_meta_success: state.chapterMetaSuccess,
        chapter_content_duration: state.chapterContentDuration,
        chapter_content_success: state.chapterContentSuccess,
        session_wait_duration: state.sessionWaitDuration,
        book_detail_start: state.bookDetailStartMs,
        chapter_meta_start: state.chapterMetaStartMs,
        chapter_content_start: state.chapterContentStartMs,
    });
}

/**
 * Step 3: 内容渲染完整耗时（正文首帧 / 失败 / 超时）
 *
 * @description 对应原 flushReaderPerfTrack 的触发时机，但只发送渲染相关字段。
 * 页面加载和接口耗时已在 markReaderPageReady 时独立发送。
 */
export function flushReaderContentRender(options: IFlushReaderPerfOptions): void {
    if (state.hasReportedContentRender || !state.tracking) {
        return;
    }

    state.hasReportedContentRender = true;
    state.tracking = false;
    clearRenderTimeout();

    // step 1 / step 2 兜底：若因异常路径（如直接进入失败分支）尚未发送，则补发
    sendPageLoad(false);
    sendApiLoad();

    const readerContentRenderDuration = roundMs(getNavElapsedMs());

    logReaderContentRender({
        book_id: state.entry.book_id,
        chapter_id: state.entry.chapter_id,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        reader_content_render_duration: readerContentRenderDuration,
        raf_paint_duration: options.rafPaintDuration ?? null,
        has_body_content: options.hasBodyContent,
        render_timeout: state.renderTimeout,
        fail_stage: state.failStage,
    });
}

function scheduleRenderTimeout(): void {
    clearRenderTimeout();
    state.renderTimeoutTimer = setTimeout(() => {
        if (state.hasReportedContentRender || !state.tracking) {
            return;
        }
        state.renderTimeout = 1;
        flushReaderContentRender({ hasBodyContent: 0 });
    }, RENDER_TIMEOUT_MS);
}

/** 首进阅读器性能是否仍在采集中（三步中至少一步未上报） */
export function isInitialReaderPerfTracking(): boolean {
    return state.tracking && !(state.hasReportedPageLoad && state.hasReportedApiLoad && state.hasReportedContentRender);
}

/**
 * 注册 pagehide 安全兜底：页面隐藏/关闭时发送尚未上报的步骤
 */
function registerPageHideListener(): void {
    if (state.pageHideListenerAttached || typeof window === 'undefined') {
        return;
    }
    state.pageHideListenerAttached = true;

    const onPageHide = () => {
        if (!state.tracking) {
            return;
        }
        // 页面隐藏时，发送所有尚未上报的步骤
        // step 1：用当前时刻作为 pageReady 近似值，标记 page_hidden
        if (!state.hasReportedPageLoad) {
            sendPageLoad(true);
        }
        // step 2：发送已采集到的接口耗时（未采集的为 null）
        if (!state.hasReportedApiLoad) {
            sendApiLoad();
        }
        // step 3：标记超时 + page_hidden，用当前时刻作为 render 结束
        if (!state.hasReportedContentRender) {
            state.renderTimeout = 1;
            state.tracking = false;
            clearRenderTimeout();
            logReaderContentRender({
                book_id: state.entry.book_id,
                chapter_id: state.entry.chapter_id,
                is_from_landing: state.entry.is_from_landing,
                is_cold_start: state.entry.is_cold_start,
                reader_content_render_duration: roundMs(getNavElapsedMs()),
                raf_paint_duration: null,
                has_body_content: 0,
                render_timeout: 1,
                fail_stage: null,
                page_hidden: 1,
            });
            state.hasReportedContentRender = true;
        }
    };

    // visibilitychange 兼容性优于 pagehide（部分 WebView 不支持 pagehide）
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            onPageHide();
        }
    });
}

/**
 * ReaderRoot mount：解析 URL 快照、冷启与会话 cookie，并开始跟踪
 */
export function initReaderPerfFromUrl(params: {
    dramaId: string;
    chapterId: string;
    searchParams: Pick<URLSearchParams, 'get'>;
}): void {
    if (typeof window === 'undefined' || state.hasReportedContentRender || state.tracking) {
        return;
    }

    const cached = loadEntrySnapshot();
    const isFromLanding = cached?.is_from_landing ?? resolveIsFromLanding(params.searchParams);
    // 冷启为 H5 整页维度，由 GlobalTrackerSync 任意 pathname 同步写入，见 @/utils/performance-log/h5-perf-session
    const isColdStart = cached?.is_cold_start ?? consumeH5ColdStart();
    const hadSessionCookie = hasSessionReadyCookie() ? 1 : 0;

    state.entry = {
        is_from_landing: isFromLanding,
        is_cold_start: isColdStart,
        had_session_cookie: hadSessionCookie,
        book_id: params.dramaId,
        chapter_id: params.chapterId,
    };
    persistEntrySnapshot(state.entry);

    state.tracking = true;
    state.readerMountMs = getNavElapsedMs();

    // Step 0：立即发送 mount 事件，捕获早期阶段数据（navigation → mount）
    const navTiming = collectColdNavigationPerfSnapshot();
    logReaderMount({
        book_id: state.entry.book_id,
        chapter_id: state.entry.chapter_id,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        had_session_cookie: state.entry.had_session_cookie,
        reader_mount_duration: roundMs(state.readerMountMs),
        static_load_duration: getDocumentDclDurationMs(),
        ttfb_duration: navTiming.ttfb_duration,
        response_duration: navTiming.response_duration,
        dom_interactive_duration: navTiming.dom_interactive_duration,
    });

    registerPageHideListener();
}

/**
 * 更新入口章节 ID（首章加载完成后调用，覆盖 URL 中的可能为空的 chapter_id）
 */
export function setEntryChapterId(chapterId: string): void {
    state.entry.chapter_id = chapterId;
    persistEntrySnapshot(state.entry);
}

/** 首章相关接口发起前（用于 session 等待子阶段） */
export function markFirstChapterApiStart(): void {
    if (!isInitialReaderPerfTracking() || state.firstChapterApiMarked) {
        return;
    }
    state.firstChapterApiMarked = true;
    if (state.entry.had_session_cookie === 0) {
        state.sessionWaitDuration = roundMs(getNavElapsedMs());
    }
}

export function markLandingApiDuration(duration: number, success: boolean): void {
    if (!isInitialReaderPerfTracking()) {
        return;
    }
    state.landingApiDuration = roundMs(duration);
    state.landingApiSuccess = success ? 1 : 0;
}

/**
 * 记录 user profile hydration（/user/profile/v1）等待耗时
 *
 * @description
 * 落地承接流程中，useLandingData 在发起 landing API 前需等待
 * hydrateUserProfileFromApi 返回的 in-flight profile 请求完成。
 * 该耗时即为 await getProfileHydrationInflight() 的等待时间。
 *
 * @param duration 等待耗时（ms），无 in-flight 请求时不调用
 */
export function markProfileHydrationWaitDuration(duration: number): void {
    if (!isInitialReaderPerfTracking()) {
        return;
    }
    const rounded = roundMs(duration);
    state.profileHydrationWaitDuration = rounded;

    // 立即发送独立埋点，避免后续页面加载失败导致该阶段数据丢失
    logReaderProfileHydration({
        book_id: state.entry.book_id,
        chapter_id: state.entry.chapter_id,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        had_session_cookie: state.entry.had_session_cookie,
        profile_hydration_wait_duration: rounded,
    });
}

export function markLandingDone(): void {
    if (!isInitialReaderPerfTracking()) {
        return;
    }
    state.landingFlowDone = 1;
    if (state.readerMountMs != null) {
        state.landingWaitDuration = roundMs(getNavElapsedMs() - state.readerMountMs);
    }
}

export function markBookDetailApiDuration(duration: number, success: boolean, startMs?: number): void {
    if (!isInitialReaderPerfTracking()) {
        return;
    }
    state.bookDetailDuration = roundMs(duration);
    state.bookDetailSuccess = success ? 1 : 0;
    if (startMs != null) {
        state.bookDetailStartMs = roundMs(startMs);
    }
}

export function markChapterMetaApiDuration(duration: number, success: boolean, startMs?: number): void {
    state.chapterMetaDuration = roundMs(duration);
    state.chapterMetaSuccess = success ? 1 : 0;
    if (startMs != null) {
        state.chapterMetaStartMs = roundMs(startMs);
    }
}

export function markChapterContentApiDuration(duration: number, success: boolean, startMs?: number): void {
    state.chapterContentDuration = roundMs(duration);
    state.chapterContentSuccess = success ? 1 : 0;
    if (startMs != null) {
        state.chapterContentStartMs = roundMs(startMs);
    }
}

/** 章节切换前重置接口耗时标记（避免缓存命中时上报上一章数据） */
export function resetChapterApiMarks(): void {
    state.chapterMetaDuration = null;
    state.chapterMetaSuccess = null;
    state.chapterContentDuration = null;
    state.chapterContentSuccess = null;
}

export interface IChapterApiTiming {
    metaDuration: number;
    metaSuccess: boolean;
    contentDuration: number | null;
    contentSuccess: boolean | null;
}

/**
 * 章节切换时上报接口耗时（仅 chapter_meta + chapter_content，不含 book_detail）
 *
 * @param chapterId 切换目标章节 ID
 * @param timing    显式传入的接口耗时（并发安全）；未传时回退读共享状态（仅首屏路径）
 */
export function reportChapterApiLoad(chapterId: string, timing?: IChapterApiTiming): void {
    logReaderApiLoad({
        book_id: state.entry.book_id,
        chapter_id: chapterId,
        is_from_landing: state.entry.is_from_landing,
        is_cold_start: state.entry.is_cold_start,
        load_type: 'chapter_switch',
        book_detail_duration: null,
        book_detail_success: null,
        chapter_meta_duration: timing ? timing.metaDuration : state.chapterMetaDuration,
        chapter_meta_success: timing ? (timing.metaSuccess ? 1 : 0) : state.chapterMetaSuccess,
        chapter_content_duration: timing ? timing.contentDuration : state.chapterContentDuration,
        chapter_content_success: timing
            ? timing.contentSuccess == null
                ? null
                : timing.contentSuccess
                  ? 1
                  : 0
            : state.chapterContentSuccess,
        session_wait_duration: null,
    });

    // 显式传入 timing 时不操作共享状态（并发安全）；回退路径仍需重置
    if (!timing) {
        resetChapterApiMarks();
    }
}

export function markReaderPerfFailStage(stage: string): void {
    if (!isInitialReaderPerfTracking()) {
        return;
    }
    state.failStage = stage;
}

/**
 * setInitReady(true) 前调用：记录页面加载完成时刻，立即发送 step 1+2，并启动渲染超时
 */
export function markReaderPageReady(): void {
    if (!isInitialReaderPerfTracking() || state.pageReadyMs != null) {
        return;
    }
    state.pageReadyMs = getNavElapsedMs();

    // 三步拆分：页面加载和接口耗时在此时已全部就绪，立即独立发送
    sendPageLoad(false);
    sendApiLoad();

    scheduleRenderTimeout();
}
